import React, { useState, useRef } from 'react';
import { supabase } from '../lib/supabase';

async function extractPdfText(file) {
  const pdfjsLib = await import('pdfjs-dist/build/pdf');
  pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  let text = '';
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    text += content.items.map(item => item.str).join(' ') + '\n';
  }
  return text;
}

// One Gemini call (via the gemini-proxy Edge Function, so the API key never
// reaches the browser) with automatic retries. Big single-shot requests were
// timing out server-side (503 on every full estimate), so the work is now
// split into small per-document calls (see extractLineItems below) and this
// helper retries transient errors with backoff.
const RETRY_DELAYS = [1000, 2500, 6000];
async function callGemini(prompt) {
  let lastError;
  for (let attempt = 0; attempt <= RETRY_DELAYS.length; attempt++) {
    if (attempt > 0) await new Promise(r => setTimeout(r, RETRY_DELAYS[attempt - 1]));
    try {
      const { data, error } = await supabase.functions.invoke('gemini-proxy', { body: { prompt } });
      if (error) {
        lastError = lastError || new Error('Network problem reaching the AI service — check your connection and try again.');
        continue;
      }
      if (!data.ok) {
        const retryable = [429, 500, 502, 503, 504].includes(data.status);
        lastError = new Error(retryable
          ? 'The AI service is busy right now — wait a minute and try again.'
          : `AI request failed (error ${data.status}).`);
        if (retryable) continue;
        throw lastError;
      }
      const raw = data.data.candidates?.[0]?.content?.parts?.[0]?.text || '';
      try {
        return JSON.parse(raw);
      } catch {
        const jsonMatch = raw.match(/\{[\s\S]*\}/);
        if (!jsonMatch) { lastError = new Error('Could not read the AI response — try again.'); continue; }
        return JSON.parse(jsonMatch[0]);
      }
    } catch (e) {
      if (e.message?.startsWith('AI request failed')) throw e; // non-retryable (bad request, auth)
      // Network failure or truncated JSON — retry
      lastError = lastError || new Error('Network problem reaching the AI service — check your connection and try again.');
    }
  }
  throw lastError;
}

// STAGE 1 — run once per PDF: turn one estimate into a compact line-item list.
// Small input + small output per call, which is what fixed the 503s.
async function extractLineItems(text, name) {
  return callGemini(`You are an expert auto body shop estimator. Extract every line item from this auto body repair estimate.

ESTIMATE (${name}):
${text}

Return a JSON object with this exact structure:
{
  "grand_total": 0.00,
  "items": [
    {
      "name": "item description",
      "operation": "REPLACE or R&I or REPAIR or REFINISH or BLEND or SUBLET or OTHER",
      "part_number": "part number if listed, or empty string",
      "quantity": 1,
      "price": 0.00,
      "is_part": true,
      "type": "OEM or LKQ or AM or USED or UNKNOWN"
    }
  ]
}

Rules:
- List EVERY line item in the estimate — do not summarize, group, or omit any
- "grand_total" is the estimate's final total (with labor/tax) if shown, otherwise the sum of items
- "is_part" is true only for physical parts (bumpers, fenders, lights, mirrors, etc.), false for labor operations and materials (paint, clear coat)
- Normalize operations: "Remove and Install"/"R/I" = "R&I"; "Repl"/"Replacement" = "REPLACE"
- Return ONLY the JSON, no other text`);
}

// STAGE 2 — one small call comparing the two compact lists (a few KB, not two full PDFs)
async function compareItemLists(doc1, doc2, name1, name2) {
  return callGemini(`You are an expert auto body shop estimator. Compare these two line-item lists extracted from two auto body repair estimates.

ESTIMATE 1 (${name1}) items:
${JSON.stringify(doc1.items)}

ESTIMATE 2 (${name2}) items:
${JSON.stringify(doc2.items)}

Return a JSON object with this exact structure:
{
  "summary": "One sentence summary of the overall difference",
  "added": [{"item": "item name", "price": 0.00, "note": "brief note"}],
  "removed": [{"item": "item name", "price": 0.00, "note": "brief note"}],
  "changed": [{"item": "item name", "price1": 0.00, "price2": 0.00, "difference": 0.00, "note": "brief note"}],
  "unchanged": [{"item": "item name", "price": 0.00}]
}

Rules:
- Account for EVERY item from both lists — do not summarize or omit any
- Match parts even if described differently (LF Fender = Left Front Fender = Driver Fender)
- Treat R&I, Remove and Install, R/I as the same operation; Replace, Repl, Replacement as the same
- "added" = items in estimate 2 but not estimate 1
- "removed" = items in estimate 1 but not estimate 2
- "changed" = same item but different price or operation type
- Return ONLY the JSON, no other text`);
}

// STAGE 2 (parts mode) — one small call selecting orderable parts from the compact list
async function selectPartsToOrder(doc) {
  return callGemini(`You are an expert auto body shop parts manager. From this line-item list extracted from an auto body repair estimate, identify all physical parts that need to be ordered.

LINE ITEMS:
${JSON.stringify(doc.items)}

Return a JSON object with this exact structure:
{
  "summary": "Brief summary of parts needed",
  "total_parts_cost": 0.00,
  "parts": [
    {
      "name": "part name",
      "part_number": "part number if listed, or empty string",
      "quantity": 1,
      "price": 0.00,
      "type": "OEM or LKQ or AM or USED or UNKNOWN",
      "note": "any relevant note like color, position, etc"
    }
  ]
}

Rules:
- Include EVERY physical part that must be ordered (bumpers, fenders, hoods, lights, mirrors, etc.) — do not omit any
- Do NOT include labor operations (R&I, refinish, blend, sublet, alignment, etc.)
- Do NOT include materials (paint, clear coat, etc.)
- Return ONLY the JSON, no other text`);
}

export default function ComparePage({ onBack, desktop }) {
  const [mode, setMode] = useState('compare'); // 'compare' or 'parts'

  // Compare mode state
  const [file1, setFile1] = useState(null);
  const [file2, setFile2] = useState(null);
  const [compareResult, setCompareResult] = useState(null);
  const ref1 = useRef();
  const ref2 = useRef();

  // Parts mode state
  const [partsFile, setPartsFile] = useState(null);
  const [partsResult, setPartsResult] = useState(null);
  const partsRef = useRef();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('');

  async function runComparison() {
    if (!file1 || !file2) return;
    setLoading(true); setError(''); setCompareResult(null);
    try {
      setStep('Reading estimate 1...');
      const text1 = await extractPdfText(file1);
      setStep('Reading estimate 2...');
      const text2 = await extractPdfText(file2);
      setStep('Listing items in estimate 1...');
      const doc1 = await extractLineItems(text1.slice(0, 60000), file1.name);
      setStep('Listing items in estimate 2...');
      const doc2 = await extractLineItems(text2.slice(0, 60000), file2.name);
      setStep('Comparing estimates...');
      const result = await compareItemLists(doc1, doc2, file1.name, file2.name);
      // Totals come straight from stage 1 — more reliable than asking the compare call
      result.total1 = doc1.grand_total || 0;
      result.total2 = doc2.grand_total || 0;
      result.difference = Math.round(((result.total2 - result.total1) + Number.EPSILON) * 100) / 100;
      setCompareResult(result);
    } catch (e) { setError(e.message); }
    setLoading(false); setStep('');
  }

  async function runPartsExtraction() {
    if (!partsFile) return;
    setLoading(true); setError(''); setPartsResult(null);
    try {
      setStep('Reading estimate...');
      const text = await extractPdfText(partsFile);
      setStep('Listing items...');
      const doc = await extractLineItems(text.slice(0, 60000), partsFile.name);
      setStep('Selecting parts to order...');
      const result = await selectPartsToOrder(doc);
      setPartsResult(result);
    } catch (e) { setError(e.message); }
    setLoading(false); setStep('');
  }

  function reset() {
    setFile1(null); setFile2(null); setCompareResult(null);
    setPartsFile(null); setPartsResult(null); setError('');
    if (ref1.current) ref1.current.value = '';
    if (ref2.current) ref2.current.value = '';
    if (partsRef.current) partsRef.current.value = '';
  }

  function switchMode(m) { setMode(m); reset(); }

  const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const diffColor = (n) => n > 0 ? 'var(--green)' : n < 0 ? 'var(--red)' : 'var(--text3)';
  const diffSign = (n) => n > 0 ? `+${fmt(n)}` : fmt(n);

  const hasResult = compareResult || partsResult;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg2)' }}>
      {/* Header */}
      <div style={{ boxShadow: 'inset 0 -9px 9px -9px rgba(0,0,0,0.6)', padding: '0 20px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', height: 72 }}>
        {hasResult && <button onClick={reset} className="btn btn-secondary" style={{ position: 'absolute', left: 16, fontSize: 13, padding: '6px 14px' }}>New</button>}
        <div style={{ fontFamily: 'var(--font-display)', fontSize: desktop ? 26 : 19, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase' }}>
          {mode === 'compare' ? 'Estimate Comparison' : 'Parts to Order'}
        </div>
        <button onClick={onBack} className="raise-3d" style={{ position: 'absolute', right: 16, background: 'var(--bg3)', border: 'none', color: 'var(--text2)', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg></button>
      </div>

      {/* Mode switcher */}
      {!hasResult && (
        <div style={{ display: 'flex', boxShadow: 'inset 0 -9px 9px -9px rgba(0,0,0,0.6)', flexShrink: 0 }}>
          {[['compare', 'Compare Estimates'], ['parts', 'Parts to Order']].map(([m, label]) => (
            <button key={m} onClick={() => switchMode(m)} style={{
              flex: 1, padding: '12px', border: 'none', cursor: 'pointer',
              background: 'transparent',
              color: mode === m ? 'var(--orange)' : 'var(--text3)',
              fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.5px',
              borderBottom: mode === m ? '2px solid var(--orange)' : '2px solid transparent',
              transition: 'all 0.15s',
            }}>{label}</button>
          ))}
        </div>
      )}

      <div style={{ flex: 1, overflowY: 'auto', padding: desktop ? '24px 28px' : '16px' }}>
        {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '12px 16px', color: 'var(--red)', fontSize: 14, marginBottom: 16 }}>{error}</div>}

        {/* ── COMPARE MODE ── */}
        {mode === 'compare' && !compareResult && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: desktop ? '1fr 1fr' : '1fr', gap: 16, marginBottom: 20 }}>
              {[
                { label: 'Original Estimate', file: file1, setFile: setFile1, ref: ref1 },
                { label: 'Supplement / New Estimate', file: file2, setFile: setFile2, ref: ref2 },
              ].map(({ label, file, setFile, ref }) => (
                <label key={label} style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  gap: 10, padding: '32px 20px',
                  background: file ? 'rgba(249,115,22,0.05)' : 'var(--bg3)',
                  border: `2px dashed ${file ? 'var(--orange)' : 'var(--border)'}`,
                  borderRadius: 14, cursor: 'pointer', transition: 'all 0.15s', minHeight: 130,
                }}>
                  <div style={{ fontSize: 30 }}>{file ? '✅' : '📄'}</div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: file ? 'var(--orange)' : 'var(--text2)' }}>{label}</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'center' }}>{file ? file.name : 'Tap to upload PDF'}</div>
                  <input ref={ref} type="file" accept=".pdf" onChange={e => setFile(e.target.files[0])} style={{ display: 'none' }} />
                </label>
              ))}
            </div>
            <button onClick={runComparison} disabled={!file1 || !file2 || loading} className="btn btn-primary"
              style={{ width: '100%', padding: '14px', fontSize: 17, fontFamily: 'var(--font-display)', fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase', opacity: (!file1 || !file2) ? 0.5 : 1 }}>
              {loading ? <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}><div className="spinner" style={{ width: 18, height: 18 }} />{step}</div> : 'Compare Estimates'}
            </button>
          </>
        )}

        {mode === 'compare' && compareResult && (
          <>
            <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 14, padding: desktop ? '20px 24px' : '16px', marginBottom: 20 }}>
              <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 12 }}>{compareResult.summary}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                {[['Estimate 1', compareResult.total1, null], ['Estimate 2', compareResult.total2, null], ['Difference', compareResult.difference, true]].map(([label, val, isDiff]) => (
                  <div key={label} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{label}</div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: desktop ? 20 : 16, fontWeight: 700, color: isDiff ? diffColor(val) : 'var(--text)' }}>{isDiff ? diffSign(val) : fmt(val)}</div>
                  </div>
                ))}
              </div>
            </div>
            {compareResult.added?.length > 0 && <Section title={`Added (${compareResult.added.length})`} color="var(--green)">{compareResult.added.map((item, i) => <Row key={i} item={item.item} note={item.note} right={<span style={{ color: 'var(--green)', fontFamily: 'var(--font-mono)', fontWeight: 700 }}>+{fmt(item.price)}</span>} desktop={desktop} />)}</Section>}
            {compareResult.removed?.length > 0 && <Section title={`Removed (${compareResult.removed.length})`} color="var(--red)">{compareResult.removed.map((item, i) => <Row key={i} item={item.item} note={item.note} right={<span style={{ color: 'var(--red)', fontFamily: 'var(--font-mono)', fontWeight: 700 }}>-{fmt(item.price)}</span>} desktop={desktop} />)}</Section>}
            {compareResult.changed?.length > 0 && <Section title={`Changed (${compareResult.changed.length})`} color="var(--yellow)">{compareResult.changed.map((item, i) => <Row key={i} item={item.item} note={item.note} right={<div style={{ textAlign: 'right' }}><div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end' }}><span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text3)', textDecoration: 'line-through' }}>{fmt(item.price1)}</span><span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 700 }}>{fmt(item.price2)}</span></div><div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: diffColor(item.difference) }}>{diffSign(item.difference)}</div></div>} desktop={desktop} />)}</Section>}
            {compareResult.unchanged?.length > 0 && <Section title={`Unchanged (${compareResult.unchanged.length})`} color="var(--text3)" collapsed>{compareResult.unchanged.map((item, i) => <Row key={i} item={item.item} right={<span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text3)' }}>{fmt(item.price)}</span>} desktop={desktop} />)}</Section>}
          </>
        )}

        {/* ── PARTS MODE ── */}
        {mode === 'parts' && !partsResult && (
          <>
            <label style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 12, padding: '40px 20px', marginBottom: 20,
              background: partsFile ? 'rgba(249,115,22,0.05)' : 'var(--bg3)',
              border: `2px dashed ${partsFile ? 'var(--orange)' : 'var(--border)'}`,
              borderRadius: 14, cursor: 'pointer', transition: 'all 0.15s',
            }}>
              <div style={{ fontSize: 36 }}>{partsFile ? '✅' : '📄'}</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: partsFile ? 'var(--orange)' : 'var(--text2)' }}>Upload Estimate PDF</div>
              <div style={{ fontSize: 13, color: 'var(--text3)', textAlign: 'center' }}>{partsFile ? partsFile.name : 'Tap to select PDF'}</div>
              <input ref={partsRef} type="file" accept=".pdf" onChange={e => setPartsFile(e.target.files[0])} style={{ display: 'none' }} />
            </label>
            <button onClick={runPartsExtraction} disabled={!partsFile || loading} className="btn btn-primary"
              style={{ width: '100%', padding: '14px', fontSize: 17, fontFamily: 'var(--font-display)', fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase', opacity: !partsFile ? 0.5 : 1 }}>
              {loading ? <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}><div className="spinner" style={{ width: 18, height: 18 }} />{step}</div> : 'Extract Parts List'}
            </button>
          </>
        )}

        {mode === 'parts' && partsResult && (
          <>
            <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 14, padding: '16px 20px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 13, color: 'var(--text3)' }}>{partsResult.summary}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{partsResult.parts?.length || 0} parts to order</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Parts Total</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 22, fontWeight: 700, color: 'var(--orange)' }}>{fmt(partsResult.total_parts_cost)}</div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(partsResult.parts || []).map((part, i) => (
                <div key={i} style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--bg4)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'var(--text3)', flexShrink: 0 }}>{i + 1}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{part.name}{part.quantity > 1 ? ` ×${part.quantity}` : ''}</div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 3, flexWrap: 'wrap' }}>
                      {part.part_number && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text3)' }}>#{part.part_number}</span>}
                      {part.type !== 'UNKNOWN' && <span style={{ fontSize: 11, fontWeight: 700, color: part.type === 'OEM' ? 'var(--blue)' : part.type === 'LKQ' ? 'var(--green)' : 'var(--text3)', background: 'var(--bg4)', padding: '1px 6px', borderRadius: 4 }}>{part.type}</span>}
                      {part.note && <span style={{ fontSize: 11, color: 'var(--text3)' }}>{part.note}</span>}
                    </div>
                  </div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 14, fontWeight: 700, color: 'var(--text)', flexShrink: 0 }}>{fmt(part.price)}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, color, children, collapsed }) {
  const [open, setOpen] = useState(!collapsed);
  return (
    <div style={{ marginBottom: 16 }}>
      <div onClick={() => setOpen(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: open ? 8 : 0, cursor: 'pointer' }}>
        <div style={{ width: 4, height: 18, borderRadius: 2, background: color, flexShrink: 0 }} />
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', flex: 1 }}>{title}</div>
        <span style={{ color: 'var(--text3)', fontSize: 13, transition: 'transform 0.2s', display: 'inline-block', transform: open ? 'rotate(180deg)' : 'none' }}>▾</span>
      </div>
      {open && <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>{children}</div>}
    </div>
  );
}

function Row({ item, note, right, desktop }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: desktop ? '11px 16px' : '10px 14px' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{item}</div>
        {note && <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{note}</div>}
      </div>
      <div style={{ flexShrink: 0 }}>{right}</div>
    </div>
  );
}
